/**
 * Table with configurable columns and load-more.
 * Visible rows are driven by parent (limit/load-more); columns use valueKey to read from ReportWidgetItem.
 */
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ChevronDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
    Box,
    Button,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { DynamicTooltip } from 'shared/ui/Tooltip';

import { ReportWidgetTableCell } from './table/ReportWidgetTableCell';
import { ReportWidgetTableLoading } from './table/ReportWidgetTableLoading';

import { useHorizontalTableScroll } from '../model/hooks/useHorizontalTableScroll';

import type {
    ReportWidgetColumnConfig,
    ReportWidgetItem,
    ReportWidgetTableOptions,
} from '../model/types';

export function getDefaultAlign(
    col: ReportWidgetColumnConfig,
): 'left' | 'right' | 'inherit' {
    if (col.align) return col.align;
    if (col.format === 'currency' || col.format === 'number') return 'right';
    return 'left';
}

interface ReportWidgetTableProps {
    items: ReportWidgetItem[];
    columns: ReportWidgetColumnConfig[];
    /** Presentation options (labels, show details column). Aligns with config + derived state. */
    options?: ReportWidgetTableOptions;
    /** Resolved colors per row (same order as items), for swatch in label column. */
    itemColors?: string[];
    loading?: boolean;
    loadingMore?: boolean;
    hasMore?: boolean;
    onLoadMore?: () => void;
    onRowClick?: (item: ReportWidgetItem) => void;
    onRowHover?: (item: ReportWidgetItem | null) => void;
    onDetailsClick?: (item: ReportWidgetItem) => void;
}

export const ReportWidgetTable = memo(
    ({
        items,
        columns,
        options,
        itemColors = [],
        loading = false,
        loadingMore = false,
        hasMore = false,
        onLoadMore,
        onRowClick,
        onRowHover,
        onDetailsClick,
    }: ReportWidgetTableProps) => {
        const { t } = useTranslation();
        const {
            containerRef: tableContainerRef,
            hasHorizontalOverflow,
            isAtScrollStart,
            isAtScrollEnd,
            handleScroll: updateHorizontalOverflowState,
            refresh: refreshHorizontalOverflowState,
        } = useHorizontalTableScroll();
        const {
            loadMoreLabel,
            loadMoreVariant = 'button',
            loadMoreCount = 0,
            showColorSwatchInLabelColumn,
            emptyMessage,
        } = options ?? {};

        useEffect(() => {
            refreshHorizontalOverflowState();
        }, [items, columns, refreshHorizontalOverflowState]);

        if (loading) {
            return <ReportWidgetTableLoading />;
        }

        return (
            <Box
                data-testclass="composable-report-widget-table"
                sx={{ width: '100%', minWidth: 0 }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        width: '100%',
                        minWidth: 0,
                    }}
                >
                    <TableContainer
                        ref={tableContainerRef}
                        onScroll={updateHorizontalOverflowState}
                        sx={{
                            width: '100%',
                            maxWidth: '100%',
                            overflowX: 'auto',
                            overflowY: 'hidden',
                        }}
                    >
                        <Table
                            size="medium"
                            stickyHeader
                            sx={{
                                width: 'max-content',
                                minWidth: '100%',
                            }}
                        >
                            <TableHead
                                sx={{
                                    '& .MuiTableCell-head': {
                                        backgroundColor: 'transparent',
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                    },
                                }}
                            >
                                <TableRow>
                                    {columns.map((col) => (
                                        <TableCell
                                            key={col.id}
                                            align={getDefaultAlign(col)}
                                            sx={{
                                                fontWeight: 500,
                                                color: 'text.secondary',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                }}
                                            >
                                                {col.label}
                                                {col.headerTooltip && (
                                                    <DynamicTooltip
                                                        content={
                                                            col.headerTooltip
                                                        }
                                                        containerStyle={{
                                                            paddingTop: '4px',
                                                        }}
                                                    >
                                                        <InfoOutlinedIcon
                                                            sx={{
                                                                fontSize:
                                                                    '14px',
                                                            }}
                                                            data-pdf-tooltip="true"
                                                        />
                                                    </DynamicTooltip>
                                                )}
                                            </Box>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.length === 0 ? (
                                    <TableRow>
                                        <TableCell
                                            colSpan={columns.length}
                                            sx={{
                                                textAlign: 'center',
                                                color: 'text.secondary',
                                                padding: 3,
                                            }}
                                        >
                                            <Typography variant="body2">
                                                {emptyMessage ??
                                                    t(
                                                        'composableReportWidget.noData',
                                                    )}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    items.map((item, rowIndex) => (
                                        <TableRow
                                            key={item.id}
                                            hover={Boolean(
                                                onRowClick || onRowHover,
                                            )}
                                            onClick={() => onRowClick?.(item)}
                                            onMouseEnter={() =>
                                                onRowHover?.(item)
                                            }
                                            onMouseLeave={() =>
                                                onRowHover?.(null)
                                            }
                                            sx={{
                                                cursor:
                                                    onRowClick || onRowHover
                                                        ? 'pointer'
                                                        : 'default',
                                                borderBottom: '1px solid',
                                                borderColor: 'divider',
                                            }}
                                        >
                                            {columns.map((col) => (
                                                <ReportWidgetTableCell
                                                    key={col.id}
                                                    item={item}
                                                    column={col}
                                                    showColorSwatchInLabelColumn={
                                                        showColorSwatchInLabelColumn
                                                    }
                                                    swatchColor={
                                                        itemColors[rowIndex] ??
                                                        item.color
                                                    }
                                                    onLabelClick={
                                                        col.isDetailsTrigger &&
                                                        onDetailsClick
                                                            ? () =>
                                                                  onDetailsClick(
                                                                      item,
                                                                  )
                                                            : undefined
                                                    }
                                                />
                                            ))}
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {hasHorizontalOverflow && !isAtScrollStart && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                bottom: 0,
                                left: 0,
                                width: 32,
                                pointerEvents: 'none',
                                background:
                                    'linear-gradient(to right, rgba(255,255,255,0.98), rgba(255,255,255,0))',
                            }}
                        />
                    )}
                    {hasHorizontalOverflow && !isAtScrollEnd && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                bottom: 0,
                                right: 0,
                                width: 32,
                                pointerEvents: 'none',
                                background:
                                    'linear-gradient(to left, rgba(255,255,255,0.98), rgba(255,255,255,0))',
                            }}
                        />
                    )}
                </Box>
                {(hasMore || loadingMore) && onLoadMore && (
                    <Box
                        sx={{
                            paddingTop: 2,
                            paddingBottom: 1,
                            paddingLeft: 0,
                            paddingRight: 0,
                            display: 'flex',
                            justifyContent: 'flex-start',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 1,
                                minHeight: 32,
                            }}
                        >
                            {loadMoreVariant === 'link' ? (
                                <Typography
                                    component="button"
                                    variant="body2"
                                    onClick={
                                        loadingMore ? undefined : onLoadMore
                                    }
                                    data-testclass="composable-report-widget-load-more"
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        cursor: loadingMore
                                            ? 'default'
                                            : 'pointer',
                                        border: 'none',
                                        background: 'none',
                                        color: 'text.secondary',
                                        textDecoration: 'none',
                                        font: 'inherit',
                                        padding: 0,
                                        fontSize: '0.8125rem',
                                        opacity: loadingMore ? 0.7 : 1,
                                        '&:hover': {
                                            color: loadingMore
                                                ? 'text.secondary'
                                                : 'text.primary',
                                        },
                                    }}
                                >
                                    {loadingMore ? (
                                        <CircularProgress
                                            size={16}
                                            sx={{ marginRight: 0.5 }}
                                        />
                                    ) : (
                                        <ChevronDownIcon
                                            sx={{ fontSize: 18 }}
                                        />
                                    )}
                                    {loadMoreLabel != null
                                        ? loadMoreLabel.replace(
                                              '{{count}}',
                                              String(loadMoreCount),
                                          )
                                        : t(
                                              'composableReportWidget.showMoreLink',
                                              {
                                                  count: loadMoreCount,
                                              },
                                          )}
                                </Typography>
                            ) : (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={onLoadMore}
                                    disabled={loadingMore}
                                    data-testclass="composable-report-widget-load-more"
                                    startIcon={
                                        loadingMore ? (
                                            <CircularProgress size={16} />
                                        ) : undefined
                                    }
                                >
                                    {loadMoreLabel ??
                                        t('composableReportWidget.loadMore')}
                                </Button>
                            )}
                        </Box>
                    </Box>
                )}
            </Box>
        );
    },
);

ReportWidgetTable.displayName = 'ReportWidgetTable';
